import React, {
	FC,
	useEffect,
	useRef,
	useState,
	useMemo,
	memo,
	ReactNode,
	isValidElement,
} from 'react';
import {
	Box,
	Image,
	List,
	ListItem,
	Skeleton,
	Text,
	Link as ChakraLink,
	Button,
	Flex,
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalFooter,
	VStack,
	Badge,
} from '@chakra-ui/react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { useNavigate } from 'react-router-dom';
import CatalogPreview from './CatalogPreview';
import CatalogSelectPreview from './CatalogSelectPreview';
import LocationCard from 'src/components/common/LocationCard';
import { useRealtimeChatContext } from '../hooks/useRealtimeChatContext';
import CustomerProfileCard from 'src/components/common/CustomerProfileCard';
import { useCatalogsContext } from 'src/contexts/catalogs/CatalogsContext';
import { useChatData } from '../contexts/ChatDataContext';
import useCopilotChatContext from '../hooks/useCopilotChatContext';
import { ChannelSelector, ChannelOptionsGrid } from 'src/components/common/ChannelSelector';
import type { ChannelType } from '../contexts/ChatDataContext';
import config from 'src/config';
// Website creation components
import WebsiteOffer from './WebsiteOffer';
import DomainSelector from './DomainSelector';
import DomainConfirmation from './DomainConfirmation';
import WebsitePreview from './WebsitePreview';

interface CustomWindow extends Window {
	fusion?: any;
}
declare const window: CustomWindow;

interface IMessageProps {
	content: string;
	onImageClick?: (src: string) => void;
	onContentResize?: () => void;
}

interface NodeProps {
	node: {
		properties?: {
			[key: string]: any;
		};
	};
	children?: ReactNode;
}

const preprocessContent = (content: string) => {
	try {
		// Removed excessive logging to reduce console noise
		// if (content.includes('catalog-preview') || content.includes('product')) {
		// 	console.log('📦 [Message] preprocessContent - Content with catalog/product:', {
		// 		contentPreview: content.substring(0, 500),
		// 		hasCatalogPreview: content.includes('catalog-preview'),
		// 		hasProduct: content.includes('product')
		// 	});
		// }

		// CRITICAL FIX: Decode HTML entities first
		// Messages loaded from database have HTML-encoded tags like &lt;div&gt;
		// We need to decode them before processing
		// Use textarea instead of div because textarea decodes entities properly
		const tempElement = document.createElement('textarea');
		tempElement.innerHTML = content;
		let processed = tempElement.value || content;

		// Only log if HTML entities were actually decoded
		// const hadEntities = content.includes('&lt;') || content.includes('&gt;');
		// if (hadEntities) {
		// 	console.log('📝 Decoded HTML entities:', {
		// 		originalLength: content.length,
		// 		decodedLength: processed.length,
		// 		preview: processed.substring(0, 200),
		// 	});
		// }

		// Remove any leading/trailing whitespace from each line while preserving structure
		processed = processed.split('\n').map(line => line.trim()).join('\n');


		// Remove leading whitespace from hyphenated HTML tags
		processed = processed.replace(
			/^[ \t]+(<[a-z0-9]+-[^>]*\/?>)/gims,
			'$1',
		);


		// CRITICAL FIX: Remove newlines and whitespace from inside HTML div blocks
		// This prevents ReactMarkdown from treating HTML with newlines as code blocks
		// Convert: <div>\n<tag />\n</div> → <div><tag /></div>
		processed = processed.replace(
			/<div>([\s\S]*?)<\/div>/g,
			(match, innerContent) => {
				// Remove all newlines and excessive whitespace from inside the div
				const cleaned = innerContent.replace(/\s*\n\s*/g, '');
				return `<div>${cleaned}</div>`;
			}
		);

		// CRITICAL FIX #2: Ensure blank lines between HTML blocks
		// ReactMarkdown requires blank lines to properly parse HTML blocks
		// Convert: </div>\n<div> → </div>\n\n<div>
		processed = processed.replace(
			/<\/div>\s*\n\s*<div>/g,
			'</div>\n\n<div>'
		);

		// Also ensure blank line before first div if preceded by text
		processed = processed.replace(
			/([^\n])\n<div>/g,
			'$1\n\n<div>'
		);

		// Ensure blank line after last div if followed by text
		processed = processed.replace(
			/<\/div>\n([^\n])/g,
			'</div>\n\n$1'
		);


		const customTagsWithAttrs = [
			'small-logo',
			'generated-logo',
			'regenerated-logo',
			'domain-selector',
			'domain-confirmation',
			'website-preview',
			'bordered-container',
			'product-suggestion',
			'suggestion-option',
		];
		
		customTagsWithAttrs.forEach(tagName => {
			// Match the tag with any attributes (self-closing or with closing tag)
			const tagRegex = new RegExp(
				`(<${tagName}[^>]*>(?:[\\s\\S]*?<\\/${tagName}>)?)`,
				'gi'
			);
			processed = processed.replace(tagRegex, '\n\n$1\n\n');
		});

		// GENERIC FIX: Unwrap ANY hyphenated custom tags from divs
		// ReactMarkdown/rehypeRaw handles custom tags better when they're not wrapped
		// Convert: <div><any-custom-tag ... /></div> → <any-custom-tag ... />
		let unwrapCount = 0;
		const unwrappedTags: string[] = [];
		const unwrappedDetails: Array<{ tagName: string; content: string }> = [];

		processed = processed.replace(
			/<div>\s*(<[a-z]+-[a-z-]+[^>]*\/?>(?:[\s\S]*?<\/[a-z]+-[a-z-]+>)?)\s*<\/div>/gi,
			(match, tagContent) => {
				unwrapCount++;
				// Extract tag name for logging
				const tagNameMatch = tagContent.match(/<([a-z]+-[a-z-]+)/i);
				if (tagNameMatch) {
					const tagName = tagNameMatch[1];
					unwrappedTags.push(tagName);
					// Only store preview content for location-preview and service-area-preview
					if (tagName === 'location-preview' || tagName === 'service-area-preview') {
						unwrappedDetails.push({
							tagName,
							content: tagContent.substring(0, 150),
						});
					}
				}
				// Return tag with blank lines around it for proper parsing
				return `\n\n${tagContent}\n\n`;
			}
		);

		// Only log if custom tags were unwrapped
		// if (unwrapCount > 0) {
		// 	console.log('📝 Unwrapped custom tags:', {
		// 		count: unwrapCount,
		// 		tags: unwrappedTags,
		// 	});
		// 	// Log details for preview tags
		// 	if (unwrappedDetails.length > 0) {
		// 		console.log('📍 Preview tags found:', unwrappedDetails);
		// 	}
		// }

		// Log channel-option tags for debugging
		if (processed.includes('<channel-option')) {
			console.log('🔧 [preprocessContent] channel-option tags found:', {
				content: processed,
				channelMatches: processed.match(/<channel-option[^>]*>.*?<\/channel-option>/g) || processed.match(/<channel-option[^>]*\/>/g),
			});
		}

		// Remove trailing newlines to prevent empty paragraphs at the end
		processed = processed.replace(/\n+$/, '');

		// Remove multiple consecutive newlines (more than 2) to prevent excessive spacing
		processed = processed.replace(/\n{3,}/g, '\n\n');

		return processed;
	} catch (e) {
		console.warn('❌ Preprocessor regex failed, returning original content:', e);
		return content;
	}
};

/**
 * Handles campaign navigation with forced sections configuration.
 * Automatically sets the first two sections as open and the first section as active.
 * Includes flag to prevent automatic scroll when coming from chat.
 *
 * @param url - The URL to navigate to
 */
const handleCampaignNavigation = (url: string) => {
	// Check if it's a campaign URL
	const campaignMatch = url.match(/\/projects\/campaigns\/([^?]+)(\?.*)?$/);
	if (campaignMatch) {
		// Always set only step 2 open and active when coming from copilot
		const sections = [false, true, false, false];
		const activeSection = 1;

		// Send navigation event before navigating
		const navigationEvent = {
			target: 'campaign',
			action: 'setNavigation',
			value: {
				sections,
				activeSection,
				preventScroll: true, // Prevent automatic scroll when coming from chat
			},
		};

		// Ensure window.fusion exists and push the event
		if (!window.fusion) {
			window.fusion = [];
		}
		if (Array.isArray(window.fusion)) {
			window.fusion.push(navigationEvent);
		} else if (window.fusion.push) {
			window.fusion.push(navigationEvent);
		}
	}
};

const loadedImagesCache = new Set<string>();

// CatalogPreviewWrapper component - for display/editing only, no selection
const CatalogPreviewWrapper = ({ catalogId }: { catalogId: string }) => {
	const { getCatalogById, isLoading, refetch } = useCatalogsContext();
	const catalog = getCatalogById(catalogId);
	const [isLocalDeleted, setIsLocalDeleted] = useState(false);
	const [hasTriedRefetch, setHasTriedRefetch] = useState(false);

	// Listen for custom events when catalog is deleted
	useEffect(() => {
		const handleCatalogDeleted = (event: CustomEvent) => {
			if (event.detail?.catalogId === catalogId) {
				setIsLocalDeleted(true);
			}
		};

		window.addEventListener(
			'catalog-deleted',
			handleCatalogDeleted as EventListener,
		);

		return () => {
			window.removeEventListener(
				'catalog-deleted',
				handleCatalogDeleted as EventListener,
			);
		};
	}, [catalogId]);

	// If catalog not found and we haven't tried refetching, try once
	useEffect(() => {
		if (!catalog && !isLoading && !hasTriedRefetch && catalogId) {
			console.log('[CatalogPreviewWrapper] Catalog not found, triggering refetch:', catalogId);
			setHasTriedRefetch(true);
			refetch();
		}
	}, [catalog, isLoading, hasTriedRefetch, catalogId, refetch]);

	// Don't render wrapper if catalog was deleted
	if (isLocalDeleted) {
		return null;
	}

	// Show loading state if catalog not found yet
	if (!catalog) {
		if (isLoading) {
			return (
				<Box w="100%" maxW="650px" p={4} borderRadius="xl" bg="gray.50">
					<Skeleton height="100px" borderRadius="lg" />
				</Box>
			);
		}
		// Catalog still not found after loading - log for debugging
		console.log('[CatalogPreviewWrapper] Catalog not found after load:', catalogId);
		return null;
	}

	return (
		<Box
			w="100%"
			maxW="650px"
			borderRadius="xl"
			boxShadow="0 0 4px rgba(0, 0, 0, 0.1)"
			_hover={{
				boxShadow: 'lg',
			}}
			transition="all 0.2s ease-in-out"
		>
			<CatalogPreview key={catalogId} catalogId={catalogId} />
		</Box>
	);
};

CatalogPreviewWrapper.displayName = 'CatalogPreviewWrapper';

// Logo Options Component - shows upload and generate buttons
const LogoOptionsComponent: FC = () => {
	const { sendMessage } = useRealtimeChatContext();
	const [isGenerating, setIsGenerating] = useState(false);
	const [selectedOption, setSelectedOption] = useState<'upload' | 'generate' | null>(null);

	const handleUpload = () => {
		setSelectedOption('upload');
		sendMessage('I want to upload my own logo');
	};

	const handleGenerate = () => {
		setSelectedOption('generate');
		setIsGenerating(true);
		sendMessage('Generate logo options for me');
	};

	return (
		<Flex gap={3} flexWrap="wrap" mt={2}>
			<Button
				bg={selectedOption === 'upload' ? 'orange.base' : 'white'}
				color={selectedOption === 'upload' ? 'white' : 'orange.base'}
				borderWidth="2px"
				borderColor="orange.base"
				_hover={{ bg: selectedOption === 'upload' ? 'orange.primary' : 'orange.lighter' }}
				size="md"
				onClick={handleUpload}
				borderRadius="xl"
				isDisabled={selectedOption !== null}
				fontFamily="'Noto Sans', 'Open Sans', sans-serif"
				fontWeight="medium"
			>
				📤 Upload my logo
			</Button>
			<Button
				bg="orange.base"
				color="white"
				_hover={{ bg: 'orange.primary' }}
				size="md"
				onClick={handleGenerate}
				isLoading={isGenerating}
				loadingText="Generating..."
				borderRadius="xl"
				isDisabled={selectedOption !== null && selectedOption !== 'generate'}
				fontFamily="'Noto Sans', 'Open Sans', sans-serif"
				fontWeight="medium"
			>
				✨ Generate logo with AI
			</Button>
		</Flex>
	);
};

LogoOptionsComponent.displayName = 'LogoOptionsComponent';

// Single Generated Logo Card Component
const GeneratedLogoCard: FC<{
	id: string;
	url: string;
	style: string;
	isSelected: boolean;
	onSelect: () => void;
	onPreview: () => void;
	onModify: () => void;
}> = ({ id, url, style, isSelected, onSelect, onPreview, onModify }) => {
	return (
		<Box
			borderRadius="xl"
			overflow="hidden"
			borderWidth={isSelected ? '3px' : '1px'}
			borderColor={isSelected ? 'orange.base' : 'gray.200'}
			bg="white"
			boxShadow={isSelected ? 'lg' : 'sm'}
			transition="all 0.2s"
			p={4}
			minW="200px"
			maxW="220px"
		>
			<VStack spacing={3}>
				<Box
					cursor="pointer"
					onClick={onPreview}
					_hover={{ opacity: 0.9 }}
					transition="opacity 0.2s"
				>
					<Image
						src={url}
						alt={`Generated logo - ${style}`}
						h="180px"
						w="180px"
						objectFit="contain"
						borderRadius="md"
					/>
				</Box>
				<Text
					fontSize="sm"
					color="gray.text"
					fontWeight="medium"
					textAlign="center"
					fontFamily="'Noto Sans', 'Open Sans', sans-serif"
				>
					{style}
				</Text>
				{isSelected ? (
					<Badge bg="orange.base" color="white" fontSize="sm" px={3} py={1}>
						✓ Selected
					</Badge>
				) : (
					<VStack spacing={2} w="100%">
						<Button
							size="sm"
							bg="orange.base"
							color="white"
							_hover={{ bg: 'orange.primary' }}
							w="100%"
							onClick={onSelect}
							fontFamily="'Noto Sans', 'Open Sans', sans-serif"
							fontWeight="medium"
						>
							Use this logo
						</Button>
						<Button
							size="sm"
							variant="outline"
							borderColor="gray.300"
							color="gray.600"
							_hover={{ bg: 'gray.50', borderColor: 'gray.400' }}
							w="100%"
							onClick={onModify}
							fontFamily="'Noto Sans', 'Open Sans', sans-serif"
							fontWeight="normal"
						>
							✏️ Modify
						</Button>
					</VStack>
				)}
			</VStack>
		</Box>
	);
};

GeneratedLogoCard.displayName = 'GeneratedLogoCard';

// Logo Preview Modal Component
const LogoPreviewModal: FC<{
	isOpen: boolean;
	onClose: () => void;
	logo: { id: string; url: string; style: string } | null;
	isSelected: boolean;
	onSelect: () => void;
	initialShowFeedback?: boolean;
}> = ({ isOpen, onClose, logo, isSelected, onSelect, initialShowFeedback = false }) => {
	const { sendMessage } = useRealtimeChatContext();
	const [showFeedback, setShowFeedback] = useState(initialShowFeedback);
	const [feedback, setFeedback] = useState('');
	const [isRegenerating, setIsRegenerating] = useState(false);

	// Update showFeedback when initialShowFeedback changes (e.g., when opening modal for modify)
	useEffect(() => {
		setShowFeedback(initialShowFeedback);
	}, [initialShowFeedback, isOpen]);

	if (!logo) return null;

	const handleSelect = () => {
		onSelect();
		onClose();
	};

	const handleRegenerateWithFeedback = () => {
		if (!feedback.trim()) return;
		setIsRegenerating(true);
		// Send message to regenerate this specific logo style with feedback
		sendMessage(`Regenerate the ${logo.style} logo with this feedback: ${feedback.trim()}`);
		setFeedback('');
		setShowFeedback(false);
		setIsRegenerating(false);
		onClose();
	};

	const handleClose = () => {
		setShowFeedback(false);
		setFeedback('');
		onClose();
	};

	return (
		<Modal isOpen={isOpen} onClose={handleClose} isCentered size="lg">
			<ModalOverlay zIndex={20000} />
			<ModalContent borderRadius="xl" mx={4} containerProps={{ zIndex: 20001 }}>
				<ModalHeader
					fontFamily="'Noto Sans', 'Open Sans', sans-serif"
					borderBottomWidth="1px"
					borderColor="gray.100"
				>
					{logo.style}
				</ModalHeader>
				<ModalBody py={6}>
					<VStack spacing={4}>
						<Box
							bg="gray.50"
							borderRadius="lg"
							p={4}
							w="100%"
							display="flex"
							justifyContent="center"
						>
							<Image
								src={logo.url}
								alt={`Generated logo - ${logo.style}`}
								maxH="350px"
								maxW="100%"
								objectFit="contain"
							/>
						</Box>
						{showFeedback && (
							<Box w="100%">
								<Text
									fontSize="sm"
									fontWeight="medium"
									mb={2}
									color="gray.600"
									fontFamily="'Noto Sans', 'Open Sans', sans-serif"
								>
									What changes would you like?
								</Text>
								<textarea
									value={feedback}
									onChange={(e) => setFeedback(e.target.value)}
									placeholder="e.g., Change the colors to blue and gold, make the icon bigger, use a bolder font..."
									style={{
										width: '100%',
										minHeight: '80px',
										padding: '12px',
										borderRadius: '8px',
										border: '1px solid #E2E8F0',
										fontFamily: "'Noto Sans', 'Open Sans', sans-serif",
										fontSize: '14px',
										resize: 'vertical',
									}}
								/>
							</Box>
						)}
					</VStack>
				</ModalBody>
				<ModalFooter borderTopWidth="1px" borderColor="gray.100" gap={3} flexWrap="wrap">
					{showFeedback ? (
						<>
							<Button variant="ghost" onClick={() => setShowFeedback(false)}>
								Cancel
							</Button>
							<Button
								bg="orange.base"
								color="white"
								_hover={{ bg: 'orange.primary' }}
								onClick={handleRegenerateWithFeedback}
								isLoading={isRegenerating}
								isDisabled={!feedback.trim()}
							>
								Regenerate
							</Button>
						</>
					) : (
						<>
							<Button
								variant="outline"
								borderColor="orange.base"
								color="orange.base"
								_hover={{ bg: 'orange.lighter' }}
								onClick={() => setShowFeedback(true)}
								size="md"
							>
								✏️ Modify this style
							</Button>
							<Box flex="1" />
							<Button variant="ghost" onClick={handleClose}>
								Close
							</Button>
							{isSelected ? (
								<Button
									bg="orange.base"
									color="white"
									_hover={{ bg: 'orange.primary' }}
									isDisabled
									leftIcon={<Text>✓</Text>}
								>
									Selected
								</Button>
							) : (
								<Button
									bg="orange.base"
									color="white"
									_hover={{ bg: 'orange.primary' }}
									onClick={handleSelect}
								>
									Use this logo
								</Button>
							)}
						</>
					)}
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};

LogoPreviewModal.displayName = 'LogoPreviewModal';

// Retry Action Component - allows user to retry failed messages
const RetryActionComponent: FC = () => {
	const { retryLastMessage, lastFailedMessage } = useCopilotChatContext();

	if (!lastFailedMessage) {
		return null;
	}

	return (
		<Box mt={3}>
			<Button
				size="sm"
				colorScheme="orange"
				variant="outline"
				leftIcon={<Text>↻</Text>}
				onClick={retryLastMessage}
			>
				Retry
			</Button>
		</Box>
	);
};

RetryActionComponent.displayName = 'RetryActionComponent';

// Generated Logo Component - handles rendering all logos from chat context
const GeneratedLogoComponent: FC<{ id: string; url: string; style: string }> = ({
	id,
	url,
	style,
}) => {
	const { sendMessage } = useRealtimeChatContext();
	const { generatedLogos, pendingLogoCount, selectedLogoId, setSelectedLogoId } = useChatData();

	// Modal state for logo preview
	const [previewLogo, setPreviewLogo] = useState<{ id: string; url: string; style: string } | null>(null);
	const [isPreviewOpen, setIsPreviewOpen] = useState(false);
	const [openWithFeedback, setOpenWithFeedback] = useState(false);

	// If there are multiple logos in context, render all of them (only once, from the first tag)
	const logoCount = generatedLogos?.length || 0;
	const isFirstTag = id === 'logo-1';

	console.log('[GeneratedLogoComponent] Rendering:', { id, logoCount, isFirstTag, selectedLogoId });

	const handleSelect = (logoId: string, logoUrl: string, style?: string) => {
		setSelectedLogoId(logoId);
		// Send a clear message that includes the URL so the AI can call select_logo with it
		const styleName = style || logoId;
		sendMessage(`I want to use this logo (${styleName}): ${logoUrl}`);
	};

	const handlePreview = (logo: { id: string; url: string; style: string }) => {
		setPreviewLogo(logo);
		setOpenWithFeedback(false);
		setIsPreviewOpen(true);
	};

	const handleModify = (logo: { id: string; url: string; style: string }) => {
		setPreviewLogo(logo);
		setOpenWithFeedback(true);
		setIsPreviewOpen(true);
	};

	const handleClosePreview = () => {
		setIsPreviewOpen(false);
		setPreviewLogo(null);
		setOpenWithFeedback(false);
	};

	// Only the first tag renders all logos to avoid duplication
	if (logoCount > 1 && !isFirstTag) {
		console.log('[GeneratedLogoComponent] Skipping render - not first tag');
		return null;
	}

	// Note: We no longer hide original logos when a regenerated logo is selected
	// Previous messages should remain visible as part of conversation history

	// If multiple logos in context (or pending), render them all in a grid
	if ((logoCount > 1 || pendingLogoCount > 0) && isFirstTag) {
		console.log('[GeneratedLogoComponent] Rendering', logoCount, 'logos from context +', pendingLogoCount, 'pending');
		return (
			<>
				<Flex gap={4} flexWrap="wrap" mt={2} justifyContent="flex-start">
					{generatedLogos.map((logo: { id: string; url: string; style: string }) => (
						<GeneratedLogoCard
							key={logo.id}
							id={logo.id}
							url={logo.url}
							style={logo.style}
							isSelected={selectedLogoId === logo.id}
							onSelect={() => handleSelect(logo.id, logo.url, logo.style)}
							onPreview={() => handlePreview(logo)}
							onModify={() => handleModify(logo)}
						/>
					))}
					{/* Skeleton placeholders for pending logos */}
					{pendingLogoCount > 0 && Array.from({ length: pendingLogoCount }).map((_, index) => (
						<Box
							key={`skeleton-${index}`}
							borderRadius="md"
							overflow="hidden"
							border="1px solid"
							borderColor="gray.200"
							bg="gray.50"
							p={2}
							width="200px"
						>
							<Skeleton height="150px" borderRadius="md" mb={2} />
							<Skeleton height="20px" width="80%" mb={2} />
							<Flex gap={2}>
								<Skeleton height="32px" flex={1} />
								<Skeleton height="32px" flex={1} />
							</Flex>
						</Box>
					))}
				</Flex>
				<LogoPreviewModal
					isOpen={isPreviewOpen}
					onClose={handleClosePreview}
					logo={previewLogo}
					isSelected={previewLogo ? selectedLogoId === previewLogo.id : false}
					onSelect={() => previewLogo && handleSelect(previewLogo.id, previewLogo.url, previewLogo.style)}
					initialShowFeedback={openWithFeedback}
				/>
			</>
		);
	}

	// Single logo fallback
	const singleLogo = { id, url, style };
	return (
		<>
			<Flex gap={4} flexWrap="wrap" mt={2} justifyContent="flex-start">
				<GeneratedLogoCard
					id={id}
					url={url}
					style={style}
					isSelected={selectedLogoId === id}
					onSelect={() => handleSelect(id, url, style)}
					onPreview={() => handlePreview(singleLogo)}
					onModify={() => handleModify(singleLogo)}
				/>
			</Flex>
			<LogoPreviewModal
				isOpen={isPreviewOpen}
				onClose={handleClosePreview}
				logo={previewLogo}
				isSelected={previewLogo ? selectedLogoId === previewLogo.id : false}
				onSelect={() => previewLogo && handleSelect(previewLogo.id, previewLogo.url, previewLogo.style)}
				initialShowFeedback={openWithFeedback}
			/>
		</>
	);
};

GeneratedLogoComponent.displayName = 'GeneratedLogoComponent';

// Regenerated Logo Component - handles rendering a single regenerated logo (after feedback modification)
const RegeneratedLogoComponent: FC<{ id: string; url: string; style: string }> = ({
	id,
	url,
	style,
}) => {
	const { sendMessage } = useRealtimeChatContext();
	const { selectedLogoId, setSelectedLogoId } = useChatData();

	// Modal state for logo preview
	const [previewLogo, setPreviewLogo] = useState<{ id: string; url: string; style: string } | null>(null);
	const [isPreviewOpen, setIsPreviewOpen] = useState(false);
	const [openWithFeedback, setOpenWithFeedback] = useState(false);

	const handleSelect = () => {
		setSelectedLogoId(id);
		// Send a clear message that includes the URL so the AI can call select_logo with it
		sendMessage(`I want to use this logo (${style}): ${url}`);
	};

	const handlePreview = () => {
		setPreviewLogo({ id, url, style });
		setOpenWithFeedback(false);
		setIsPreviewOpen(true);
	};

	const handleModify = () => {
		setPreviewLogo({ id, url, style });
		setOpenWithFeedback(true);
		setIsPreviewOpen(true);
	};

	const handleClosePreview = () => {
		setIsPreviewOpen(false);
		setPreviewLogo(null);
		setOpenWithFeedback(false);
	};

	// If a different logo is selected, hide this regenerated logo
	if (selectedLogoId && selectedLogoId !== id) {
		console.log('[RegeneratedLogoComponent] Hiding - different logo selected:', selectedLogoId);
		return null;
	}

	// Render this regenerated logo (either unselected or selected)
	return (
		<>
			<Flex gap={4} flexWrap="wrap" mt={2} justifyContent="flex-start">
				<GeneratedLogoCard
					id={id}
					url={url}
					style={style}
					isSelected={selectedLogoId === id}
					onSelect={handleSelect}
					onPreview={handlePreview}
					onModify={handleModify}
				/>
			</Flex>
			<LogoPreviewModal
				isOpen={isPreviewOpen}
				onClose={handleClosePreview}
				logo={previewLogo}
				isSelected={previewLogo ? selectedLogoId === previewLogo.id : false}
				onSelect={() => previewLogo && handleSelect()}
				initialShowFeedback={openWithFeedback}
			/>
		</>
	);
};

RegeneratedLogoComponent.displayName = 'RegeneratedLogoComponent';

// Logo Options Grid - wraps generated logos in a horizontal layout (kept for backwards compatibility)
const LogoOptionsGridComponent: FC<{ children: ReactNode }> = ({ children }) => {
	return (
		<Flex gap={3} flexWrap="wrap" mt={2} justifyContent="flex-start">
			{children}
		</Flex>
	);
};

LogoOptionsGridComponent.displayName = 'LogoOptionsGridComponent';

const ChannelComponent: FC<{ channelType: ChannelType }> = ({
	channelType,
}) => {
	const { selectedChannel, setSelectedChannel } = useChatData();
	const { sendMessage } = useRealtimeChatContext();

	const handleChannelSelect = (channel: ChannelType) => {
		setSelectedChannel(channel);
		// Send message to chat indicating the selection
		const channelNames: Record<ChannelType, string> = {
			online: 'At home',
			instore: 'From my stores',
			both: 'Both (At home and from my stores)',
			digital: 'Digital Goods',
		};
		sendMessage(channelNames[channel]);
	};

	return (
		<ChannelSelector
			selectedChannel={selectedChannel || undefined}
			onChannelSelect={handleChannelSelect}
			type={channelType}
		/>
	);
};

/** Grid component that shows all channel options in 2x2 layout */
const ChannelOptionsComponent: FC = () => {
	const { selectedChannel, setSelectedChannel } = useChatData();
	const { sendMessage } = useRealtimeChatContext();

	const handleChannelSelect = (channel: ChannelType) => {
		setSelectedChannel(channel);
		// Send message to chat indicating the selection
		const channelNames: Record<ChannelType, string> = {
			online: 'At home',
			instore: 'From my stores',
			both: 'Both (At home and from my stores)',
			digital: 'Digital Goods',
		};
		sendMessage(channelNames[channel]);
	};

	return (
		<ChannelOptionsGrid
			selectedChannel={selectedChannel || undefined}
			onChannelSelect={handleChannelSelect}
		/>
	);
};

const Message: FC<IMessageProps> = ({
	content,
	onImageClick,
	onContentResize,
}) => {
	const navigate = useNavigate();
	const { sendMessage } = useRealtimeChatContext();
	const contentRef = useRef<HTMLDivElement>(null);
	const { getCatalogById } = useCatalogsContext();
	// Extract bordered-container selection state at component level for reliable access
	const { clickedBorderedContent, setClickedBorderedContent } = useChatData();

	// Check if contexts are ready - if not, don't render yet
	const contextsReady = Boolean(sendMessage && getCatalogById);

	// Force component to remount when contexts become available
	const [markdownKey, setMarkdownKey] = useState(0);

	useEffect(() => {
		if (contextsReady) {
			// Small delay to ensure contexts are fully initialized, then force remount
			const timer = setTimeout(() => {
				setMarkdownKey(prev => prev + 1);
			}, 100);
			return () => clearTimeout(timer);
		}
	}, [contextsReady]);

	useEffect(() => {
		if (!contentRef.current || !onContentResize) return;

		// Debounce resize events to avoid rapid-fire scroll calls
		let resizeTimeout: NodeJS.Timeout | null = null;
		let lastHeight = 0;

		const resizeObserver = new ResizeObserver((entries) => {
			if (resizeTimeout) clearTimeout(resizeTimeout);

			// Only trigger resize if height actually changed significantly
			const entry = entries[0];
			const newHeight = entry.contentRect.height;
			const heightDiff = Math.abs(newHeight - lastHeight);

			// Ignore small height changes (< 5px) to reduce unnecessary scrolls
			if (heightDiff < 5) return;

			lastHeight = newHeight;
			resizeTimeout = setTimeout(() => {
				onContentResize();
			}, 100); // Increased debounce to 100ms for smoother scrolling with widgets
		});

		resizeObserver.observe(contentRef.current);
		return () => {
			if (resizeTimeout) clearTimeout(resizeTimeout);
			resizeObserver.disconnect();
		};
	}, [onContentResize]);

	const components = useMemo(
		() => ({
			a: ({ href, children }: { href: string; children: ReactNode }) => {
				if (!href) return <>{children}</>;
				const isInternal = href.startsWith('/');
				return (
					<ChakraLink
						color="blue.500"
						cursor="pointer"
						onClick={(e) => {
							e.preventDefault();
							if (isInternal) {
								handleCampaignNavigation(href);
								navigate(href);
							} else {
								window.open(href, '_blank', 'noopener');
							}
						}}
					>
						{children}
					</ChakraLink>
				);
			},
			img: ({ src, alt }: { src: string; alt?: string }) => (
				<LazyImage src={src} alt={alt} onImageClick={onImageClick} />
			),
			p: ({ children }: { children: ReactNode }) => {
				// Don't render empty paragraphs (caused by extra newlines in markdown)
				if (!children || (typeof children === 'string' && !children.trim())) {
					return null;
				}
				return (
					<Box as="div" fontSize={{ base: '16px', md: '14px' }} sx={{ '&:last-child': { mb: 0 }, mb: 2 }}>{children}</Box>
				);
			},
			ul: ({ children }: { children: ReactNode }) => (
				<List spacing={2} styleType="disc" pl={4}>
					{children}
				</List>
			),
			ol: ({ children }: { children: ReactNode }) => (
				<List as="ol" spacing={2} styleType="decimal" pl={4}>
					{children}
				</List>
			),
			li: ({ children }: { children: ReactNode }) => (
				<ListItem>{children}</ListItem>
			),
			'link-button': ({ node }: NodeProps) => {
				const { label, to } = node.properties as any;
				return (
					<Box display="flex" justifyContent="center">
						<Button
							variant="orangeSolid"
							onClick={() => {
								if (typeof to === 'string') {
									handleCampaignNavigation(to);
									navigate(to);
								}
							}}
						>
							{label}
						</Button>
					</Box>
				);
			},
			'catalog-preview': ({ node }: NodeProps) => {
				const catalogId = node.properties?.catalogid as string;
				const { products } = useChatData();

				// Try to decode base64 catalogId (backend may send name encoded as base64)
				let decodedName = '';
				try {
					decodedName = atob(catalogId);
				} catch {
					// Not base64, use as-is
				}

				console.log('[catalog-preview] Rendering:', {
					catalogId,
					decodedName,
					productsCount: products?.length || 0,
					productIds: products?.map((p: any) => p.catalogId || p.id).slice(0, 5),
					productNames: products?.map((p: any) => p.name).slice(0, 5),
				});

				if (!catalogId) {
					console.error('❌ [Message] Missing required props for catalog-preview:', {
						catalogId,
						properties: node.properties
					});
					return null;
				}

				// First, try to find the specific catalog by ID in chat context
				// This handles freshly added products before global context syncs
				// Include base64 decoding to match product names
				const catalogFromContext = products?.find(
					(p: any) =>
						p.catalogId === catalogId ||
						p.id === catalogId ||
						p.name === catalogId ||
						(decodedName && p.name === decodedName)
				);

				console.log('[catalog-preview] Lookup result:', {
					catalogId,
					decodedName,
					foundInContext: !!catalogFromContext,
					foundName: catalogFromContext?.name,
				});

				if (catalogFromContext) {
					const actualCatalogId = catalogFromContext.catalogId || catalogFromContext.id;
					return (
						<Box
							w="100%"
							maxW="650px"
							mb={3}
							borderRadius="xl"
							boxShadow="0 0 4px rgba(0, 0, 0, 0.1)"
							_hover={{
								boxShadow: 'lg',
							}}
							transition="all 0.2s ease-in-out"
						>
							<CatalogPreview catalogId={actualCatalogId} />
						</Box>
					);
				}

				// Try index-based lookup from chat context (for freshly added products)
				const catalogIndex = parseInt(catalogId);
				if (!isNaN(catalogIndex) && products && products[catalogIndex]) {
					const catalog = products[catalogIndex];
					const actualCatalogId = catalog.catalogId || catalog.id;
					return (
						<Box
							w="100%"
							maxW="650px"
							mb={3}
							borderRadius="xl"
							boxShadow="0 0 4px rgba(0, 0, 0, 0.1)"
							_hover={{
								boxShadow: 'lg',
							}}
							transition="all 0.2s ease-in-out"
						>
							<CatalogPreview catalogId={actualCatalogId} />
						</Box>
					);
				}

				// Fallback: try ID-based lookup from global context
				return <CatalogPreviewWrapper catalogId={catalogId} />;
			},
			'catalog-select-preview': ({ node }: NodeProps) => {
				const catalogId = node.properties?.catalogid as string;
				const explicitSelected =
					node.properties?.selected === 'true' ||
					node.properties?.selected === true;
				const catalog = getCatalogById(catalogId);
				const { selectedCatalogId, setSelectedCatalogId, products } = useChatData();
				const [isLocalDeleted, setIsLocalDeleted] = useState(false);

				// Try to decode base64 catalogId (backend may send name encoded as base64)
				let decodedName = '';
				try {
					decodedName = atob(catalogId);
				} catch {
					// Not base64, use as-is
				}

				console.log('[catalog-select-preview] Rendering:', {
					catalogId,
					decodedName,
					explicitSelected,
					hasCatalog: !!catalog,
					productsCount: products?.length || 0,
					productNames: products?.map((p: any) => p.name).slice(0, 5),
				});

				if (!catalogId) {
					console.error('Missing required props for catalog-select-preview:', {
						catalogId,
					});
					return null;
				}

				// Listen for catalog deletion events
				React.useEffect(() => {
					const handleCatalogDeleted = (event: CustomEvent) => {
						if (event.detail?.catalogId === catalogId) {
							setIsLocalDeleted(true);
						}
					};

					window.addEventListener(
						'catalog-deleted',
						handleCatalogDeleted as EventListener,
					);

					return () => {
						window.removeEventListener(
							'catalog-deleted',
							handleCatalogDeleted as EventListener,
						);
					};
				}, [catalogId]);

				// Check if catalog exists in chat products (scraped catalogs)
				// Include base64 decoding to match product names
				const productFromChat = products?.find(
					(p: any) =>
						p.catalogId === catalogId ||
						p.id === catalogId ||
						p.name === catalogId ||
						(decodedName && p.name === decodedName)
				);

				console.log('[catalog-select-preview] Lookup result:', {
					catalogId,
					decodedName,
					foundInApi: !!catalog,
					foundInChat: !!productFromChat,
					productFromChatName: productFromChat?.name,
				});

				// Don't render if catalog doesn't exist in API or chat, or was deleted
				if ((!catalog && !productFromChat) || isLocalDeleted) {
					console.log('[catalog-select-preview] Not rendering - no catalog found or deleted');
					return null;
				}

				// Auto-select first catalog if none is selected and this catalog has explicit selection
				React.useEffect(() => {
					if (!selectedCatalogId && explicitSelected) {
						setSelectedCatalogId(catalogId);
					}
				}, [
					catalogId,
					selectedCatalogId,
					setSelectedCatalogId,
					explicitSelected,
				]);

				// Use chat context to determine selection state dynamically
				const isSelected = selectedCatalogId === catalogId;

				const handleClick = () => {
					// Update selection in chat context
					setSelectedCatalogId(catalogId);

					const catalogName = catalog?.name || productFromChat?.name;
					if (catalogName) {
						sendMessage(`Let's use ${catalogName}`);
					}
				};

				return (
					<Box w="100%" maxW="500px">
						<CatalogSelectPreview
							catalogId={catalogId}
							selected={isSelected}
							onClick={handleClick}
						/>
					</Box>
				);
			},
			'location-preview': ({ node }: NodeProps) => {
				const { locationid } = node.properties as any;

				if (locationid === undefined) {
					console.error('location-preview: Missing locationid property');
					return null;
				}

				// Render the specific location by its index
				return <LocationPreviewComponent locationId={parseInt(locationid)} />;
			},
			'customer-profile-card': ({ node }: NodeProps) => {
				const { profileid } = node.properties as any;

				if (profileid === undefined) {
					console.error('customer-profile-card: Missing profileid property');
					return null;
				}

				// Render the specific profile by its index
				return <CustomerProfileCardComponent profileIndex={parseInt(profileid)} />;
			},
			'service-area-preview': ({ node }: NodeProps) => {
				const { serviceareaid } = node.properties as any;

				if (serviceareaid === undefined) {
					console.error('service-area-preview: Missing serviceareaid property');
					return null;
				}

				// Render the specific service area by its index
				return (
					<ServiceAreaPreviewComponent
						serviceAreaId={parseInt(serviceareaid)}
					/>
				);
			},

			'bordered-container': ({ node, children }: NodeProps) => {
				const { clickable, action, selected } = node.properties as any;
				
				// Default to clickable for confirmation flow
				const isClickable = clickable !== 'false';
				
				// Extract text content for comparison - handle multiple nested structures
				const extractTextContent = (child: ReactNode): string => {
					if (typeof child === 'string') return child.trim();
					if (typeof child === 'number') return String(child);
					if (Array.isArray(child)) return child.map(extractTextContent).join('').trim();
					if (isValidElement(child)) {
						const props = child.props as any;
						if (props?.children) return extractTextContent(props.children);
					}
					return '';
				};
				const textContent = extractTextContent(children);
				
				// Check if this option is selected:
				// 1. Backend says it's selected (default selection)
				// 2. OR user clicked on it (matches clickedBorderedContent from parent scope)
				const isBackendSelected = selected === 'true' || selected === true;
				const isUserClicked = clickedBorderedContent !== null && textContent === clickedBorderedContent;
				const isSelected = isUserClicked || (isBackendSelected && clickedBorderedContent === null);
				
				// Debug log for selection state
				if (action === 'select') {
					console.log('📦 [bordered-container] Selection check:', {
						textContent,
						clickedBorderedContent,
						isBackendSelected,
						isUserClicked,
						isSelected,
					});
				}

				const handleClick = () => {
					if (isClickable) {
						// action="select" sends the content (for industry/product selection)
						// action="confirm" or default sends "yes" (for field confirmation)
						if (action === 'select') {
							if (textContent) {
								// Update selection state (from parent scope)
								setClickedBorderedContent(textContent);
								sendMessage(textContent);
							}
						} else {
							sendMessage('yes');
						}
					}
				};

				return (
					<Box
						border="1px solid"
						borderColor={isSelected ? 'blue.500' : 'gray.300'}
						borderRadius="md"
						bg={isSelected ? 'blue.50' : 'gray.50'}
						p={4}
						fontSize="14px"
						cursor={isClickable ? 'pointer' : 'default'}
						onClick={handleClick}
						_hover={
							isClickable
								? {
										bg: isSelected ? 'blue.100' : 'gray.100',
										borderColor: isSelected ? 'blue.600' : 'gray.400',
									}
								: {}
						}
						transition="all 0.2s ease-in-out"
					>
						{children}
					</Box>
				);
			},
			'suggestion-option': ({ node, children }: NodeProps) => {
				const { value } = node.properties as any;
				const displayValue = value || (typeof children === 'string' ? children.trim() : '');

				const handleClick = () => {
					if (displayValue) {
						sendMessage(displayValue);
					}
				};

				return (
					<Box
						as="button"
						display="inline-flex"
						alignItems="center"
						justifyContent="center"
						border="2px solid"
						borderColor="orange.base"
						borderRadius="xl"
						bg="white"
						color="orange.base"
						px={4}
						py={2}
						mr={2}
						mb={2}
						fontSize="14px"
						fontWeight="medium"
						fontFamily="'Noto Sans', 'Open Sans', sans-serif"
						cursor="pointer"
						onClick={handleClick}
						_hover={{
							bg: 'orange.lighter',
							borderColor: 'orange.primary',
							transform: 'translateY(-1px)',
						}}
						_active={{
							bg: 'orange.base',
							color: 'white',
							transform: 'translateY(0)',
						}}
						transition="all 0.15s ease-in-out"
					>
						{children || displayValue}
					</Box>
				);
			},
			// Product suggestion - selectable card similar to catalog-select-preview
			// Used when showing product/service suggestions with radio-style selection
			'product-suggestion': ({ node, children }: NodeProps) => {
				const { index: suggestionIndex, value, selected } = node.properties as any;
				const displayValue = value || (typeof children === 'string' ? children.trim() : '');
				const indexNum = parseInt(suggestionIndex || '0', 10);
				const isInitiallySelected = selected === 'true' || selected === true;
				
				// Use shared state for selection (first one selected by default)
				const { selectedSuggestionIndex, setSelectedSuggestionIndex } = useChatData();
				
				// Auto-select first suggestion if none is selected
				React.useEffect(() => {
					if (selectedSuggestionIndex === null && isInitiallySelected) {
						setSelectedSuggestionIndex(indexNum);
					}
				}, [indexNum, selectedSuggestionIndex, setSelectedSuggestionIndex, isInitiallySelected]);
				
				const isSelected = selectedSuggestionIndex === indexNum;

				const handleClick = () => {
					// Update selection state
					setSelectedSuggestionIndex(indexNum);
					// Send message with the selected value
					if (displayValue) {
						sendMessage(displayValue);
					}
				};

				return (
					<Box
						w="100%"
						maxW="500px"
						mb={2}
						border="2px solid"
						borderColor={isSelected ? 'orange.base' : 'gray.200'}
						borderRadius="xl"
						bg={isSelected ? 'orange.50' : 'white'}
						p={4}
						cursor="pointer"
						onClick={handleClick}
						_hover={{
							borderColor: isSelected ? 'orange.primary' : 'gray.300',
							bg: isSelected ? 'orange.100' : 'gray.50',
							boxShadow: 'md',
						}}
						transition="all 0.2s ease-in-out"
						position="relative"
					>
						<Flex align="center" gap={3}>
							{/* Radio-style indicator */}
							<Box
								w="20px"
								h="20px"
								borderRadius="full"
								border="2px solid"
								borderColor={isSelected ? 'orange.base' : 'gray.300'}
								bg={isSelected ? 'orange.base' : 'white'}
								display="flex"
								alignItems="center"
								justifyContent="center"
								flexShrink={0}
							>
								{isSelected && (
									<Box
										w="8px"
										h="8px"
										borderRadius="full"
										bg="white"
									/>
								)}
							</Box>
							{/* Content */}
							<Text
								fontSize="14px"
								fontWeight={isSelected ? '600' : 'normal'}
								color={isSelected ? 'gray.800' : 'gray.600'}
								fontFamily="'Noto Sans', 'Open Sans', sans-serif"
							>
								{children || displayValue}
							</Text>
						</Flex>
					</Box>
				);
			},
			'small-logo': ({ node }: NodeProps) => {
				const { url } = node.properties as any;
				console.log('🖼️ [small-logo] Rendering with properties:', node.properties);
				
				if (!url) {
					console.warn('🖼️ [small-logo] No URL provided, not rendering');
					return null;
				}

				return (
					<Box
						position="relative"
						display="inline-block"
						mb={3}
						borderRadius="lg"
						overflow="hidden"
						border="1px solid"
						borderColor="orange.300"
						bg="orange.50"
						p={3}
					>
						<Image
							src={url}
							alt="Logo (too small)"
							maxH="120px"
							maxW="200px"
							objectFit="contain"
							borderRadius="md"
							opacity={0.8}
						/>
						<Badge
							position="absolute"
							top={1}
							right={1}
							bg="orange.500"
							color="white"
							fontSize="10px"
							px={2}
							py={0.5}
							borderRadius="md"
						>
							⚠️
						</Badge>
					</Box>
				);
			},
			'channel-option': ({ node }: NodeProps) => {
				const { type } = node.properties as any;
				console.log('📺 [channel-option component] Rendering channel:', { type, nodeProperties: node.properties });
				return <ChannelComponent channelType={type} />;
			},
			'channel-options': () => {
				console.log('📺 [channel-options component] Rendering all channels in grid');
				return <ChannelOptionsComponent />;
			},
			'logo-options': () => {
				return <LogoOptionsComponent />;
			},
			'logo-options-grid': ({ children }: { children: ReactNode }) => {
				return <LogoOptionsGridComponent>{children}</LogoOptionsGridComponent>;
			},
			'generated-logo': ({ node }: NodeProps) => {
				const { id, url, style } = node.properties as any;
				return <GeneratedLogoComponent id={id} url={url} style={style} />;
			},
			'regenerated-logo': ({ node }: NodeProps) => {
				const { id, url, style } = node.properties as any;
				return <RegeneratedLogoComponent id={id} url={url} style={style} />;
			},
			'retry-action': () => {
				return <RetryActionComponent />;
			},
			// Website creation components
			'website-offer': () => {
				return <WebsiteOffer />;
			},
			'domain-selector': ({ node }: NodeProps) => {
				const { domains } = node.properties as any;
				return <DomainSelector domains={domains} />;
			},
			'domain-confirmation': ({ node }: NodeProps) => {
				const { domain } = node.properties as any;
				return <DomainConfirmation domain={domain} />;
			},
			'website-preview': ({ node }: NodeProps) => {
				const { status, domain, url } = node.properties as any;
				return <WebsitePreview status={status} domain={domain} url={url} />;
			},
			div: ({ children }: { children: ReactNode }) => {
				return <div>{children}</div>;
			},
		}),
		[navigate, onImageClick, sendMessage, getCatalogById, contextsReady],
	);

	const processedContent = preprocessContent(content.trim());

	// Check if we have custom tags that need context
	const hasCustomTags =
		processedContent.includes('<channel-option') ||
		processedContent.includes('<channel-options') ||
		processedContent.includes('<catalog-preview') ||
		processedContent.includes('<link-button') ||
		processedContent.includes('<customer-profile-card') ||
		processedContent.includes('<location-preview') ||
		processedContent.includes('<service-area-preview') ||
		processedContent.includes('<logo-options') ||
		processedContent.includes('<logo-options-grid') ||
		processedContent.includes('<generated-logo') ||
		processedContent.includes('<regenerated-logo') ||
		processedContent.includes('<retry-action') ||
		processedContent.includes('<website-offer') ||
		processedContent.includes('<domain-selector') ||
		processedContent.includes('<domain-confirmation') ||
		processedContent.includes('<website-preview') ||
		processedContent.includes('<suggestion-option') ||
		processedContent.includes('<product-suggestion') ||
		processedContent.includes('<small-logo');

	// Don't render until contexts are ready to avoid showing raw HTML
	if (!contextsReady) {
		if (hasCustomTags) {
			console.warn('⚠️ Contexts not ready, waiting to render custom tags');
		}
		return <div ref={contentRef} style={{ fontSize: '14px' }} />;
	}

	return (
		<div ref={contentRef} style={{ fontSize: '14px' }}>
			<ReactMarkdown
				key={markdownKey}
				remarkPlugins={[remarkGfm]}
				rehypePlugins={[rehypeRaw]}
				components={components as any}
			>
				{processedContent}
			</ReactMarkdown>
		</div>
	);
};

const LazyImage: FC<{
	src: string;
	alt?: string;
	onImageClick?: (src: string) => void;
}> = memo(({ src, alt, onImageClick }) => {
	const [isLoaded, setIsLoaded] = useState(loadedImagesCache.has(src));

	useEffect(() => {
		if (isLoaded) {
			loadedImagesCache.add(src);
		}
	}, [isLoaded, src]);

	return (
		<Box
			position="relative"
			borderRadius="lg"
			overflow="hidden"
			cursor="pointer"
			maxW="200px"
			minW="200px"
		>
			{!isLoaded && <Skeleton h="150px" w="full" />}
			<Image
				src={src}
				alt={alt}
				onError={() => setIsLoaded(true)}
				onLoad={() => setIsLoaded(true)}
				onClick={onImageClick ? () => onImageClick(src) : undefined}
				display={isLoaded ? 'block' : 'none'}
				borderRadius="lg"
				objectFit="cover"
				transition="opacity 0.3s ease-in-out"
			/>
		</Box>
	);
});

LazyImage.displayName = 'LazyImage';

// Separate component for location preview to use context properly
const LocationPreviewComponent: FC<{
	locationId: number;
}> = ({ locationId }) => {
	const {
		locations,
		setEditingLocation,
		setLocationModalOpen,
		removeLocation,
		isLocationRemoved,
	} = useChatData();

	// Delete confirmation modal state
	const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] =
		useState(false);
	const [locationToDelete, setLocationToDelete] = useState<{
		id: string;
		name: string;
	} | null>(null);
	const [isDeletingLocation, setIsDeletingLocation] = useState(false);

	// Get location from context by index
	const displayLocation = locations[locationId];
	if (!displayLocation) {
		// Location was removed or doesn't exist at this index - don't render
		return null;
	}

	const itemLocationId = displayLocation.id || displayLocation.name;
	const isRemoved = isLocationRemoved(itemLocationId);

	// If the location was marked as removed, don't render
	if (isRemoved) {
		return null;
	}

	const address = {
		street: displayLocation.address?.street || '',
		city: displayLocation.address?.city || '',
		state: displayLocation.address?.state || '',
		postalCode: displayLocation.address?.postalCode || '',
		country: displayLocation.address?.country || 'USA',
		lat: displayLocation.address?.lat || 40.7128,
		long: displayLocation.address?.long || -74.006,
		placeId: displayLocation.address?.placeId,
	};

	const handleEdit = () => {
		setEditingLocation(displayLocation);
		setLocationModalOpen(true);
	};

	const handleRemove = () => {
		const locationId = displayLocation.id || displayLocation.name;
		setLocationToDelete({ id: locationId, name: displayLocation.name });
		setIsConfirmDeleteModalOpen(true);
	};

	const confirmDeleteLocation = async () => {
		if (!locationToDelete) return;

		setIsDeletingLocation(true);
		try {
			removeLocation(locationToDelete.id);
			setIsConfirmDeleteModalOpen(false);
			setLocationToDelete(null);
		} catch (error) {
			console.error('Error removing location:', error);
		} finally {
			setIsDeletingLocation(false);
		}
	};

	return (
		<>
			<div style={{ display: isRemoved ? 'none' : 'block', width: '100%', maxWidth: '600px', marginBottom: '16px' }}>
				<LocationCard
					title={displayLocation.name}
					address={address}
					type="location"
					height="300px"
					maxWidth="600px"
					onEdit={handleEdit}
					onRemove={handleRemove}
				/>
			</div>

			{/* Delete Location Confirmation Modal */}
			<Modal
				isOpen={isConfirmDeleteModalOpen}
				onClose={() => {
					setIsConfirmDeleteModalOpen(false);
					setLocationToDelete(null);
				}}
				isCentered
			>
				<ModalOverlay zIndex={20000} />
				<ModalContent
					containerProps={{
						zIndex: 20001,
					}}
				>
					<ModalHeader>Confirm Deletion</ModalHeader>
					<ModalBody>
						<Text>
							Are you sure you want to delete the location{' '}
							<Text as="span" fontWeight="bold">
								"{locationToDelete?.name}"
							</Text>
							?
						</Text>
					</ModalBody>
					<ModalFooter>
						<Flex w="full" justifyContent="flex-end" gap={2}>
							<Button
								onClick={() => {
									setIsConfirmDeleteModalOpen(false);
									setLocationToDelete(null);
								}}
								isDisabled={isDeletingLocation}
							>
								Cancel
							</Button>
							<Button
								onClick={confirmDeleteLocation}
								colorScheme="secondary"
								isLoading={isDeletingLocation}
								loadingText="Deleting..."
							>
								Delete
							</Button>
						</Flex>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</>
	);
};

// Separate component for service area preview to use context properly
const ServiceAreaPreviewComponent: FC<{
	serviceAreaId: number;
}> = ({ serviceAreaId }) => {
	const {
		serviceAreas,
		setEditingServiceArea,
		setServiceAreaModalOpen,
		removeServiceArea,
		isServiceAreaRemoved,
	} = useChatData();

	// Delete confirmation modal state
	const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] =
		useState(false);
	const [serviceAreaToDelete, setServiceAreaToDelete] = useState<{
		id: string;
		name: string;
	} | null>(null);
	const [isDeletingServiceArea, setIsDeletingServiceArea] = useState(false);

	// Geocoded coordinates for geographic service areas
	const [geocodedCoords, setGeocodedCoords] = useState<{ lat: number; lng: number } | null>(null);
	const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);

	// Get service area from context by index
	const displayServiceArea = serviceAreas[serviceAreaId];

	// Load Google Maps API
	useEffect(() => {
		const loadGoogleMaps = () => {
			// Check if API key is configured
			if (!config.google?.mapsApiKey) {
				console.log('[ServiceAreaPreview] Google Maps API key not configured');
				return;
			}

			// Check if already loaded
			if (window.google && window.google.maps && window.google.maps.Geocoder) {
				console.log('[ServiceAreaPreview] Google Maps already loaded');
				setIsGoogleMapsLoaded(true);
				return;
			}

			// Check if script is already loading
			const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
			if (existingScript) {
				// Script is loading, wait for it to be ready
				let retryCount = 0;
				const maxRetries = 50; // 5 seconds max
				const checkLoaded = () => {
					if (window.google && window.google.maps && window.google.maps.Geocoder) {
						console.log('[ServiceAreaPreview] Google Maps finished loading (waited)');
						setIsGoogleMapsLoaded(true);
					} else if (retryCount < maxRetries) {
						retryCount++;
						setTimeout(checkLoaded, 100);
					} else {
						console.log('[ServiceAreaPreview] Google Maps loading timeout');
					}
				};
				checkLoaded();
				return;
			}

			// Create and load script
			console.log('[ServiceAreaPreview] Loading Google Maps script');
			const callbackName = `initGoogleMapsForServiceArea_${Date.now()}`;
			(window as any)[callbackName] = () => {
				console.log('[ServiceAreaPreview] Google Maps callback fired');
				// Wait a bit for Geocoder to be available
				let retryCount = 0;
				const checkApiReady = () => {
					if (window.google && window.google.maps && window.google.maps.Geocoder) {
						console.log('[ServiceAreaPreview] Google Maps fully loaded');
						setIsGoogleMapsLoaded(true);
					} else if (retryCount < 50) {
						retryCount++;
						setTimeout(checkApiReady, 100);
					}
				};
				checkApiReady();
			};

			const script = document.createElement('script');
			script.src = `https://maps.googleapis.com/maps/api/js?key=${config.google.mapsApiKey}&libraries=places&loading=async&callback=${callbackName}`;
			script.async = true;
			script.defer = true;
			script.onerror = () => {
				console.error('[ServiceAreaPreview] Failed to load Google Maps API');
			};
			document.head.appendChild(script);
		};

		loadGoogleMaps();
	}, []);

	// Geocode geographic service areas that don't have coordinates
	useEffect(() => {
		console.log('[ServiceAreaPreview] Geocode useEffect triggered', {
			hasDisplayServiceArea: !!displayServiceArea,
			name: displayServiceArea?.name,
			type: displayServiceArea?.type,
			geocodedCoords,
			isGoogleMapsLoaded,
		});

		if (!displayServiceArea) {
			console.log('[ServiceAreaPreview] No displayServiceArea, returning');
			return;
		}

		// Skip if already geocoded
		if (geocodedCoords) {
			console.log('[ServiceAreaPreview] Already geocoded, skipping');
			return;
		}

		// Skip if Google Maps not loaded yet
		if (!isGoogleMapsLoaded) {
			console.log('[ServiceAreaPreview] Google Maps not loaded yet');
			return;
		}

		// Check if it's a geographic service area without coordinates
		// Use type field if set, otherwise infer from presence of geographic object
		const isGeographic = displayServiceArea.type === 'geographic' ||
			(!displayServiceArea.type && displayServiceArea.geographic);
		const hasNoCoords = !displayServiceArea.address?.lat && !displayServiceArea.address?.long &&
			!displayServiceArea.geographic?.lat && !displayServiceArea.geographic?.lng;

		console.log('[ServiceAreaPreview] Conditions:', {
			isGeographic,
			hasNoCoords,
			originalType: displayServiceArea.type,
			hasGeographicObj: !!displayServiceArea.geographic,
		});

		if (isGeographic && hasNoCoords) {
			// Build search query from geographic entity
			const searchQuery = displayServiceArea.geographic?.displayName ||
				displayServiceArea.geographic?.entityName ||
				displayServiceArea.name;
			console.log('[ServiceAreaPreview] Search query:', searchQuery);
			if (!searchQuery) return;

			const country = displayServiceArea.geographic?.country || 'US';
			const fullQuery = `${searchQuery}, ${country}`;

			console.log('[ServiceAreaPreview] Geocoding:', fullQuery);

			const geocoder = new window.google.maps.Geocoder();
			geocoder.geocode({ address: fullQuery }, (results: any, status: any) => {
				console.log('[ServiceAreaPreview] Geocode response:', { status, resultsCount: results?.length });
				if (status === 'OK' && results && results[0]) {
					const location = results[0].geometry.location;
					console.log('[ServiceAreaPreview] Geocoded result:', fullQuery, location.lat(), location.lng());
					setGeocodedCoords({
						lat: location.lat(),
						lng: location.lng(),
					});
				} else {
					console.log('[ServiceAreaPreview] Geocoding failed:', fullQuery, status);
				}
			});
		} else {
			console.log('[ServiceAreaPreview] Not geocoding - isGeographic:', isGeographic, 'hasNoCoords:', hasNoCoords);
		}
	}, [displayServiceArea, geocodedCoords, isGoogleMapsLoaded]);

	if (!displayServiceArea) {
		// Service area was removed or doesn't exist at this index - don't render
		return null;
	}

	const itemServiceAreaId = displayServiceArea.id || displayServiceArea.name;
	const isRemoved = isServiceAreaRemoved(itemServiceAreaId);

	// If the service area was marked as removed, don't render
	if (isRemoved) {
		return null;
	}

	// Determine coordinates - use geocoded for geographic, or from address, or default
	let lat: number | string = 40.7128; // NYC default
	let long: number | string = -74.006;

	if (displayServiceArea.type === 'geographic') {
		// For geographic service areas, use geocoded coords or geographic coords
		if (geocodedCoords) {
			lat = geocodedCoords.lat;
			long = geocodedCoords.lng;
		} else if (displayServiceArea.geographic?.lat && displayServiceArea.geographic?.lng) {
			lat = displayServiceArea.geographic.lat;
			long = displayServiceArea.geographic.lng;
		}
	} else if (displayServiceArea.address?.lat && displayServiceArea.address?.long) {
		// For radius service areas, use address coords
		lat = displayServiceArea.address.lat;
		long = displayServiceArea.address.long;
	}

	const address = {
		street: displayServiceArea.address?.street || '',
		city: displayServiceArea.address?.city || displayServiceArea.geographic?.entityName || '',
		state: displayServiceArea.address?.state || displayServiceArea.geographic?.state || '',
		postalCode: displayServiceArea.address?.postalCode || '',
		country: displayServiceArea.address?.country || displayServiceArea.geographic?.country || 'USA',
		lat,
		long,
	};

	const handleEdit = () => {
		console.log('🎯 [ServiceAreaPreview] Edit clicked:', {
			displayServiceArea,
			serviceAreaId,
			hasSetEditingServiceArea: !!setEditingServiceArea,
			hasSetServiceAreaModalOpen: !!setServiceAreaModalOpen
		});
		setEditingServiceArea(displayServiceArea);
		setServiceAreaModalOpen(true);
		console.log('🎯 [ServiceAreaPreview] Modal state updated');
	};

	const handleRemove = () => {
		const serviceAreaId = displayServiceArea.id || displayServiceArea.name;
		setServiceAreaToDelete({
			id: serviceAreaId,
			name: displayServiceArea.name,
		});
		setIsConfirmDeleteModalOpen(true);
	};

	const confirmDeleteServiceArea = async () => {
		if (!serviceAreaToDelete) return;

		setIsDeletingServiceArea(true);
		try {
			removeServiceArea(serviceAreaToDelete.id);
			setIsConfirmDeleteModalOpen(false);
			setServiceAreaToDelete(null);
		} catch (error) {
			console.error('Error removing service area:', error);
		} finally {
			setIsDeletingServiceArea(false);
		}
	};

	// Get entity type for geographic service areas (infer type if not set)
	const isGeoType = displayServiceArea.type === 'geographic' ||
		(!displayServiceArea.type && displayServiceArea.geographic);
	const entityType = isGeoType
		? displayServiceArea.geographic?.entityType
		: undefined;

	return (
		<>
			<div
				style={{
					display: isRemoved ? 'none' : 'block',
					width: '100%',
					minWidth: '336px',
					maxWidth: '480px',
					marginBottom: '16px',
				}}
			>
				<LocationCard
					key={`${displayServiceArea.name}-${address.lat}-${address.long}`}
					title={displayServiceArea.name}
					address={address}
					type="service-area"
					radius={!isGeoType && displayServiceArea.radius ? Number(displayServiceArea.radius) : undefined}
					entityType={entityType}
					height="300px"
					maxWidth="480px"
					onEdit={handleEdit}
					onRemove={handleRemove}
				/>
			</div>

			{/* Delete Service Area Confirmation Modal */}
			<Modal
				isOpen={isConfirmDeleteModalOpen}
				onClose={() => {
					setIsConfirmDeleteModalOpen(false);
					setServiceAreaToDelete(null);
				}}
				isCentered
			>
				<ModalOverlay zIndex={20000} />
				<ModalContent
					containerProps={{
						zIndex: 20001,
					}}
				>
					<ModalHeader>Confirm Deletion</ModalHeader>
					<ModalBody>
						<Text>
							Are you sure you want to delete the service area{' '}
							<Text as="span" fontWeight="bold">
								"{serviceAreaToDelete?.name}"
							</Text>
							?
						</Text>
					</ModalBody>
					<ModalFooter>
						<Flex w="full" justifyContent="flex-end" gap={2}>
							<Button
								onClick={() => {
									setIsConfirmDeleteModalOpen(false);
									setServiceAreaToDelete(null);
								}}
								isDisabled={isDeletingServiceArea}
							>
								Cancel
							</Button>
							<Button
								onClick={confirmDeleteServiceArea}
								colorScheme="secondary"
								isLoading={isDeletingServiceArea}
								loadingText="Deleting..."
							>
								Delete
							</Button>
						</Flex>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</>
	);
};

// Separate component for customer profile card to use context properly
const CustomerProfileCardComponent: FC<{
	profileIndex: number;
}> = ({ profileIndex }) => {
	const { customerProfiles: chatCustomerProfiles } = useChatData();
	const {
		setEditingCustomerProfile,
		setCustomerProfileModalOpen,
		removeCustomerProfile,
	} = useCopilotChatContext();

	// Monitor global update timestamp to force re-render when profile is updated
	const [updateTimestamp, setUpdateTimestamp] = React.useState(Date.now());

	// Delete confirmation modal state (local to this component)
	const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] = useState(false);
	const [profileToDelete, setProfileToDelete] = useState<any>(null);

	React.useEffect(() => {
		const checkForUpdates = () => {
			const globalTimestamp = (window as any).__customerProfileUpdateTimestamp;
			if (globalTimestamp && globalTimestamp > updateTimestamp) {
				setUpdateTimestamp(globalTimestamp);
			}
		};

		const interval = setInterval(checkForUpdates, 100);
		return () => clearInterval(interval);
	}, [updateTimestamp]);

	// Get profile from chat context by index
	const profile = chatCustomerProfiles?.[profileIndex];

	if (!profile) {
		return null;
	}

	const handleEdit = async () => {
		console.log('🎯🎯🎯 [handleEdit] START - Edit button clicked');
		console.log('🎯 [handleEdit] Profile from context:', JSON.stringify({
			_id: profile._id,
			name: profile.name,
			customer_rules: (profile as any).customer_rules,
			customer_tones: (profile as any).customer_tones,
			hasRules: !!(profile as any).customer_rules?.length,
			hasTones: !!(profile as any).customer_tones?.length,
		}, null, 2));
		try {
			let enrichedProfile = profile;

			// Always try to fetch the latest profile from API to ensure we have all data
			// (customer_rules, customer_tones, etc.)
			if (profile._id) {
				console.log('🎯 [handleEdit] Fetching profile from API with _id:', profile._id);
				try {
					const { getCustomerProfiles } = await import('src/services/account');
					const list = await getCustomerProfiles();
					console.log('🎯 [handleEdit] API returned', list.length, 'profiles');
					const found = list.find((p: any) => p._id === profile._id);
					if (found) {
						console.log('🎯 [handleEdit] Found in API:', JSON.stringify({
							_id: found._id,
							name: found.name,
							customer_rules: found.customer_rules,
							customer_tones: found.customer_tones,
							hasRules: !!found.customer_rules?.length,
							hasTones: !!found.customer_tones?.length,
						}, null, 2));
						// Merge API data with local profile data, preserving local fields that API doesn't return
						enrichedProfile = {
							...profile, // Start with local data (may have customer_rules, customer_tones from copilot)
							...found, // Override with API data
							// Ensure customer_rules and customer_tones are preserved from either source
							customer_rules: found.customer_rules?.length > 0
								? found.customer_rules
								: (profile as any).customer_rules || [],
							customer_tones: found.customer_tones?.length > 0
								? found.customer_tones
								: (profile as any).customer_tones || [],
						} as any;
						console.log('🎯 [handleEdit] After merge - enrichedProfile:', JSON.stringify({
							_id: (enrichedProfile as any)._id,
							name: (enrichedProfile as any).name,
							customer_rules: (enrichedProfile as any).customer_rules,
							customer_tones: (enrichedProfile as any).customer_tones,
							hasRules: !!(enrichedProfile as any).customer_rules?.length,
							hasTones: !!(enrichedProfile as any).customer_tones?.length,
						}, null, 2));
					} else {
						console.warn('🎯 [handleEdit] Profile NOT found in API, using local data only');
						console.log('🎯 [handleEdit] Local profile has rules:', !!(profile as any).customer_rules?.length);
						console.log('🎯 [handleEdit] Local profile has tones:', !!(profile as any).customer_tones?.length);
					}
				} catch (e) {
					// ignore fetch errors, continue with current profile
					console.error('🎯 [handleEdit] API fetch error:', e);
				}
			} else {
				console.log('🎯 [handleEdit] No _id on profile, using local data only');
			}

			console.log('🎯 [handleEdit] FINAL enrichedProfile being sent to modal:', JSON.stringify({
				_id: (enrichedProfile as any)._id,
				name: (enrichedProfile as any).name,
				customer_rules: (enrichedProfile as any).customer_rules,
				customer_tones: (enrichedProfile as any).customer_tones,
			}, null, 2));

			// Set editing profile and open modal via context
			setEditingCustomerProfile(enrichedProfile as any);
			setCustomerProfileModalOpen(true);
			console.log('🎯🎯🎯 [handleEdit] END - Modal should now be open');
		} catch (e) {
			console.error('Failed to open CustomerProfileFormModal:', e);
		}
	};

	const handleDelete = () => {
		setProfileToDelete(profile);
		setIsConfirmDeleteModalOpen(true);
	};

	const confirmDelete = async () => {
		if (!profileToDelete) return;
		try {
			const { deleteCustomerProfile } = await import('src/services/account');
			await deleteCustomerProfile(profileToDelete._id);
			// Remove from context state
			removeCustomerProfile(profileToDelete._id);
			setIsConfirmDeleteModalOpen(false);
			setProfileToDelete(null);
		} catch (e) {
			console.error('Failed to delete customer profile:', e);
		}
	};

	return (
		<>
			<Box w="100%" maxW="500px" mb={3}>
				<CustomerProfileCard
					customerProfile={profile}
					onEdit={handleEdit}
					onDelete={handleDelete}
					showActions
					actionType="delete"
				/>
			</Box>

			{/* Delete Confirmation Modal */}
			<Modal
				isOpen={isConfirmDeleteModalOpen}
				onClose={() => {
					setIsConfirmDeleteModalOpen(false);
					setProfileToDelete(null);
				}}
				isCentered
			>
				<ModalOverlay zIndex={20000} />
				<ModalContent containerProps={{ zIndex: 20001 }}>
					<ModalHeader>Confirm Deletion</ModalHeader>
					<ModalBody>
						<Text>
							Are you sure you want to delete the customer profile{' '}
							<Text as="span" fontWeight="bold">
								"{profileToDelete?.name}"
							</Text>
							?
						</Text>
					</ModalBody>
					<ModalFooter>
						<Flex w="full" justifyContent="flex-end" gap={2}>
							<Button
								onClick={() => {
									setIsConfirmDeleteModalOpen(false);
									setProfileToDelete(null);
								}}
							>
								Cancel
							</Button>
							<Button onClick={confirmDelete} colorScheme="secondary">
								Delete
							</Button>
						</Flex>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</>
	);
};

export default Message;
